import React from 'react';
import { TARGET_BUFFER_OPTIONS } from '../../utils/presetMappings';

/**
 * Target-price strike positioning for the 0DTE module.
 *
 * Instead of picking a moneyness band, the user names the price they think the
 * underlying is heading to. The screener then pulls either the single nearest
 * listed strike (no buffer) or every strike inside a symmetric buffer around
 * that price.
 *
 * The current-price fetch is owned by the parent form so both the desktop and
 * mobile forms can share one effect; this component only presents it.
 */
const fmtPrice = (value) => {
  const num = parseFloat(value);
  if (!Number.isFinite(num)) return '--';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function TargetPriceStrikeSection({
  ticker,
  currentPrice,
  priceLoading = false,
  priceError = null,
  onRetryPrice,
  targetPrice,
  onTargetPriceChange,
  bufferPct,
  onBufferPctChange,
  isMobile = false,
}) {
  const target = parseFloat(targetPrice);
  const hasTarget = Number.isFinite(target) && target > 0;
  const hasPrice = Number.isFinite(parseFloat(currentPrice)) && parseFloat(currentPrice) > 0;
  const isCustomBuffer =
    bufferPct != null && !TARGET_BUFFER_OPTIONS.some(opt => opt.value === bufferPct);

  // Distance of the target from spot, so the user can sanity-check a typo
  // before running (a fat-fingered 6600 on a $660 underlying is obvious here).
  const movePct = hasTarget && hasPrice
    ? ((target - parseFloat(currentPrice)) / parseFloat(currentPrice)) * 100
    : null;

  const chipClass = (isSelected) =>
    `px-3 py-1.5 text-xs rounded transition-colors ${
      isSelected ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
    }`;

  return (
    <div className="space-y-3">
      {/* Current underlying price */}
      <div className="flex items-center gap-2 text-sm">
        {!ticker ? (
          <span className="text-gray-400">Select a ticker to see its current price.</span>
        ) : priceLoading ? (
          <span className="text-gray-400">Fetching {ticker} price…</span>
        ) : priceError ? (
          <>
            <span className="text-red-400">Couldn't fetch {ticker}'s price.</span>
            {onRetryPrice && (
              <button
                type="button"
                onClick={onRetryPrice}
                className="px-2 py-0.5 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600"
              >
                Retry
              </button>
            )}
          </>
        ) : hasPrice ? (
          <>
            <span className="text-gray-400">{ticker} current price</span>
            <span className="font-bold text-blue-500">{fmtPrice(currentPrice)}</span>
          </>
        ) : (
          <span className="text-gray-400">No price available for {ticker}.</span>
        )}
      </div>

      {/* Target price input */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-300 block">
          Target underlying price
        </label>
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm">$</span>
          <input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={targetPrice ?? ''}
            onChange={(e) => onTargetPriceChange(e.target.value)}
            placeholder={hasPrice ? parseFloat(currentPrice).toFixed(2) : '0.00'}
            className="bg-[#1f2937] border border-[#333] rounded px-3 py-2 text-sm text-white
                       focus:outline-none focus:border-[#218aff]"
            style={{ width: isMobile ? '100%' : 140 }}
          />
          {movePct != null && (
            <span className={`text-xs ${movePct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {movePct >= 0 ? '+' : ''}{movePct.toFixed(2)}% from spot
            </span>
          )}
        </div>
        {targetPrice !== '' && targetPrice != null && !hasTarget && (
          <p className="text-xs text-red-400">Enter a price greater than zero.</p>
        )}
      </div>

      {/* Buffer chips */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-300 block">
          Strikes to include around the target
        </label>
        <div className="flex flex-wrap gap-1.5 items-center">
          {TARGET_BUFFER_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => onBufferPctChange(opt.value)}
              className={chipClass(bufferPct === opt.value)}
            >
              {opt.label}
            </button>
          ))}
          <div className="flex items-center gap-1">
            <span className={`text-xs ${isCustomBuffer ? 'text-blue-400' : 'text-gray-400'}`}>±</span>
            <input
              type="number"
              min="0.01"
              max="50"
              step="0.25"
              inputMode="decimal"
              value={isCustomBuffer ? +(bufferPct * 100).toFixed(2) : ''}
              onChange={(e) => {
                const raw = parseFloat(e.target.value);
                if (!Number.isFinite(raw)) {
                  onBufferPctChange(null);
                  return;
                }
                onBufferPctChange(Math.min(50, Math.max(0.01, raw)) / 100);
              }}
              placeholder="custom"
              className={`bg-[#1f2937] border rounded px-2 py-1 text-xs text-white w-20
                          focus:outline-none focus:border-[#218aff] ${
                            isCustomBuffer ? 'border-[#218aff]' : 'border-[#333]'
                          }`}
            />
            <span className="text-xs text-gray-400">%</span>
          </div>
        </div>
      </div>

      {/* Live preview of what will be pulled */}
      <p className="text-xs text-gray-400">
        {!hasTarget ? (
          'Enter a target price to preview which strikes will be pulled.'
        ) : bufferPct == null ? (
          <>Pulling the <strong className="text-gray-200">nearest listed strike</strong> to {fmtPrice(target)} for each selected expiration.</>
        ) : (
          <>Pulling every listed strike between{' '}
            <strong className="text-gray-200">{fmtPrice(target * (1 - bufferPct))}</strong> and{' '}
            <strong className="text-gray-200">{fmtPrice(target * (1 + bufferPct))}</strong> for each selected expiration.
          </>
        )}
      </p>
    </div>
  );
}
